import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bookmark, Trash2, FolderOpen, Loader2, LogIn } from "lucide-react";
import { useAuth } from "../../context/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SavedScenarios({ calc, values, onLoad }) {
  const { user, openAuthModal } = useAuth();
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/scenarios?calculator=${encodeURIComponent(calc.slug)}`, { withCredentials: true });
      setItems(r.data.scenarios || []);
    } catch { /* noop */ }
  }, [user, calc.slug]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!name.trim()) { toast.error("Give this scenario a name"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/scenarios`, { calculator: calc.slug, name: name.trim(), values }, { withCredentials: true });
      setName("");
      await load();
      toast.success("Scenario saved to your account");
    } catch {
      toast.error("Couldn't save — please try again");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/scenarios/${id}`, { withCredentials: true }); await load(); } catch { /* noop */ }
  };

  if (!user) {
    return (
      <div data-testid="saved-scenarios-signedout" className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Bookmark className="h-4 w-4 text-brand" /> Save your scenarios
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Sign in to name and save multiple scenarios for this calculator and revisit them anytime — free.</p>
        <button onClick={() => openAuthModal()} data-testid="signin-google-inline"
          className="mt-3 inline-flex items-center gap-2 rounded-full border border-brand px-4 py-2 text-sm font-semibold text-brand transition-colors hover:bg-brand/5">
          <LogIn className="h-4 w-4" /> Sign in to save
        </button>
      </div>
    );
  }

  return (
    <div data-testid="saved-scenarios" className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <Bookmark className="h-4 w-4 text-brand" /> Your saved scenarios
      </div>
      <div className="mt-3 flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name this scenario (e.g. Aggressive plan)"
          data-testid="scenario-name-input"
          className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand" />
        <button onClick={save} disabled={busy} data-testid="scenario-save-btn"
          className="inline-flex items-center gap-1.5 rounded-lg bg-action px-4 py-2 text-sm font-semibold text-white hover:bg-action-hover disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bookmark className="h-4 w-4" />} Save
        </button>
      </div>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">No saved scenarios yet. Adjust the inputs and save your first one.</p>
      ) : (
        <ul className="mt-3 divide-y divide-border">
          {items.map((s) => (
            <li key={s.id} className="flex items-center justify-between py-2" data-testid={`scenario-item-${s.id}`}>
              <span className="text-sm font-medium text-foreground">{s.name}</span>
              <span className="flex items-center gap-3">
                <button onClick={() => onLoad(s.values)} data-testid={`scenario-load-${s.id}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:text-action">
                  <FolderOpen className="h-3.5 w-3.5" /> Load
                </button>
                <button onClick={() => remove(s.id)} data-testid={`scenario-delete-${s.id}`}
                  className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-red-600">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
